<template>
  <layout>
    <page-header :header="$t('general.new_layout_vehicle_additional_prices')" />
    <div v-if="!currentDivision" class="content">
      <p class="text-muted">{{ $t('company.select_division') }}</p>
    </div>
    <div v-else class="content vehicle-additional-prices">
      <p v-if="loading" class="text-muted py-3">
        {{ $t('general.loading') }}
      </p>
      <b-card v-else class="mb-0">
        <div class="form-group row">
          <label for="" class="col-sm-4 col-form-label text-sm-right"></label>
          <div class="col-sm-8">
            <hint-text :value="$t('vehicles.field_groups.additional_info')" />
          </div>
        </div>
        <div v-if="singleRideMode">
          <additional-fields-form
            v-model="forms.city_taxi_pricing"
            :field-keys="additionalFieldKeys"
            :currency-symbol="selectedCurrency.symbol"
          />
          <div class="mt-3">
            <b-button
              v-if="$allows('vehicles.update')"
              variant="primary"
              :disabled="saving"
              @click="saveTab('city_taxi_pricing')"
            >
              {{ $t('general.save') }}
            </b-button>
          </div>
        </div>

        <div v-else>
          <b-tabs v-model="tabIndex" content-class="mt-3" pills card>
            <b-tab
              v-for="tab in pricingTabs"
              :key="tab.key"
              :title="tab.title"
            >
              <additional-fields-form
                :key="tab.key"
                v-model="forms[tab.key]"
                :field-keys="additionalFieldKeys"
                :currency-symbol="selectedCurrency.symbol"
              />
              <div class="mt-3">
                <b-button
                  v-if="$allows('vehicles.update')"
                  variant="primary"
                  :disabled="saving"
                  @click="saveTab(tab.key)"
                >
                  {{ $t('general.save') }}
                </b-button>
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </b-card>
    </div>
  </layout>
</template>

<script>
import VehicleAdditionalServicePricesApi from './Api'
import AdditionalFieldsForm from './AdditionalFieldsForm.vue'
import HintText from '../components/HintText.vue'

const ADDITIONAL_FIELD_KEYS = [
  'meet_greet',
  'disabled_place',
  'baby_seat',
  'booster_seat',
  'child_seat',
  'bike',
  'ski_equipment_fee',
  'stopway_minimum',
  'passenger_fee_min',
  'passenger_fee',
  'animal_fee',
  'waiting_time_per_minute',
]

function emptyAdditionalForm() {
  return ADDITIONAL_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {})
}

export default {
  name: 'VehicleAdditionalPricesIndex',

  components: {
    AdditionalFieldsForm,
    HintText,
  },

  data() {
    return {
      loading: false,
      saving: false,
      tabIndex: 0,
      additionalFieldKeys: ADDITIONAL_FIELD_KEYS,
      forms: {
        pricing: emptyAdditionalForm(),
        city_taxi_pricing: emptyAdditionalForm(),
        delivery_pricing: emptyAdditionalForm(),
      },
    }
  },

  computed: {
    singleRideMode() {
      return !!this.$settings.one_price_ride_type
    },
    pricingTabs() {
      return [
        { key: 'pricing', title: this.$t('enums.ride_type.transfer') },
        { key: 'city_taxi_pricing', title: this.$t('enums.ride_type.city_taxi') },
        { key: 'delivery_pricing', title: this.$t('enums.ride_type.delivery') },
      ]
    },
    selectedCurrency() {
      return this.$root.$account.currency ?? this.$settings.base_currency
    },
  },

  watch: {
    currentDivision: {
      handler(div) {
        if (div) {
          this.load()
        }
      },
      immediate: true,
    },
  },

  methods: {
    hydrateForms(data) {
      const fill = (target, source) => {
        for (const key of ADDITIONAL_FIELD_KEYS) {
          const v = source && source[key]
          target[key] = v != null && v !== '' ? v : ''
        }
      }
      fill(this.forms.pricing, data.pricing || {})
      fill(this.forms.city_taxi_pricing, data.city_taxi_pricing || {})
      fill(this.forms.delivery_pricing, data.delivery_pricing || {})
    },

    buildPayload(form) {
      const additional = {}
      for (const key of ADDITIONAL_FIELD_KEYS) {
        const raw = form[key]
        if (raw === '' || raw === null || raw === undefined) {
          additional[key] = null
        } else {
          const n = Number(raw)
          additional[key] = Number.isFinite(n) ? n : null
        }
      }
      return additional
    },

    async load() {
      if (!this.currentDivision) {
        return
      }
      this.loading = true
      try {
        const api = new VehicleAdditionalServicePricesApi().setParameter(
          'division_id',
          this.currentDivision.id,
        )
        const res = await this.$uiBlocker(api.fetchForm(), this.$el)
        this.hydrateForms(res)
      } catch (e) {
        this.$toast.error(e?.data?.message || e?.message || 'Error')
      } finally {
        this.loading = false
      }
    },

    async saveTab(pricingKey) {
      if (!this.currentDivision || !this.$allows('vehicles.update')) {
        return
      }
      this.saving = true
      try {
        const api = new VehicleAdditionalServicePricesApi()
        const res = await this.$uiBlocker(
          api.save({
            division_id: this.currentDivision.id,
            pricing_key: pricingKey,
            additional: this.buildPayload(this.forms[pricingKey]),
          }),
          this.$el,
        )
        this.$toast.success(res.message)
        await this.load()
      } catch (e) {
        if (e?.data?.errors) {
          this.$toast.error(
            Object.values(e.data.errors)
              .flat()
              .join(', '),
          )
        } else {
          this.$toast.error(e?.data?.message || e?.message || 'Error')
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.vehicle-additional-prices >>> .card-header-pills {
  margin: -0.25rem 0;
}
</style>
